import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { AlertCircle, Search } from 'lucide-react';
import Navigation from '@/components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = (s) => {
  const t = (s || '').toLowerCase();
  if (['out', 'injured reserve', 'ir'].includes(t)) return 'bg-red-500/20 text-red-400 border-red-500/30';
  if (t === 'doubtful') return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
  if (t === 'questionable') return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
  return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
};

const NflInjuries = () => {
  const [injuries, setInjuries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    axios.get(`${API}/injuries/nfl`)
      .then(r => setInjuries(r.data.injuries || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = injuries.filter(i =>
    !query ||
    (i.player_name || '').toLowerCase().includes(query.toLowerCase()) ||
    (i.team || '').toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
              <AlertCircle className="h-8 w-8 text-red-400" />
              NFL Injuries
            </h1>
            <p className="text-muted-foreground mt-1">Latest injury reports across the league</p>
          </div>

          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search player or team..."
              className="pl-9 bg-[#0f1e36] border-[#1e3a5f] text-white"
              data-testid="nfl-injury-search-input"
            />
          </div>

          <div className="mb-4">
            <Badge variant="outline" className="border-brand-cyan text-brand-cyan" data-testid="nfl-injury-count">{filtered.length} Reports</Badge>
          </div>

          {loading ? (
            <div className="space-y-2">{[1, 2, 3, 4, 5].map(i => <div key={i} className="h-16 skeleton rounded" />)}</div>
          ) : filtered.length === 0 ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]">
              <CardContent className="p-12 text-center text-muted-foreground" data-testid="nfl-no-injuries">No injury reports found.</CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filtered.map((inj, i) => (
                <Card key={i} className="bg-[#0f1e36] border-[#1e3a5f]" data-testid={`nfl-injury-row-${i}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-white font-semibold">{inj.player_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {inj.team}{inj.position ? ` · ${inj.position}` : ''}{inj.injury ? ` · ${inj.injury}` : ''}
                      </div>
                      {inj.comment && <div className="text-xs text-muted-foreground mt-1 max-w-xl">{inj.comment}</div>}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${statusColor(inj.status)}`}>
                      {(inj.status || 'Unknown').toUpperCase()}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NflInjuries;
